// Auth helpers for Sales Cabinet.
//
// Token storage: localStorage key `sales.token` holds the user_<uuid> token
// returned from POST /auth/login. `sales.user` holds the serialised UserInfo
// so we don't need to re-fetch on every page reload.
//
// Security note: meta-admin's login flow was intentionally fixed to return
// `user_<uuid>` instead of the raw Shanyrak JWT (see backend/pkg/api/http/
// auth.go), so storing it in localStorage is no worse than a regular session
// cookie. We still treat it as sensitive — never log, never include in URLs.

export type UserInfo = {
  id: string;
  email: string;
  name: string;
  role: string;
};

const TOKEN_KEY = "sales.token";
const USER_KEY = "sales.user";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getUser(): UserInfo | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UserInfo;
  } catch {
    return null;
  }
}

export function setAuth(token: string, user: UserInfo): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

export function clearAuth(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function isAuthed(): boolean {
  const u = getUser();
  // Only admin role is allowed in Sales Cabinet — engineer/partner users
  // have their own GEO UI at geo.evimiz.ai.
  return !!u && u.role === "admin" && !!getToken();
}

export async function login(email: string, password: string): Promise<UserInfo> {
  const resp = await fetch("/auth/login", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const data = (await resp.json()) as {
    valid: boolean;
    token_hash?: string;
    user?: UserInfo;
    error?: string;
  };
  if (!resp.ok || !data.valid || !data.token_hash || !data.user) {
    throw new Error(data.error || "Login failed");
  }
  if (data.user.role !== "admin") {
    // Defence in depth — backend would also reject non-admin access via
    // @admin directive, but we'd rather fail explicitly here than show an
    // empty Sales Cabinet.
    throw new Error("Sales Cabinet требует роль admin");
  }
  setAuth(data.token_hash, data.user);
  return data.user;
}

export function logout(): void {
  clearAuth();
  window.location.href = "/login";
}
